// Shop Service for Multi-Tenant Architecture
// Handles shop-specific data isolation and management

import { apiConfig } from '../config/api';

export interface Shop {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  owner?: string;
  type: 'grocery' | 'medical' | 'electronics' | 'clothing' | 'pharmacy' | 'specialty' | 'bakery' | 'restaurant';
  description?: string;
  phone?: string;
  email?: string;
  logo?: string;
  logo_url?: string;
  image?: string;
  image_url?: string;
  photo?: string;
  banner_url?: string;
  theme?: {
    primary_color: string;
    secondary_color: string;
    accent_color: string;
  };
  theme_config?: {
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
  };
  rating?: number;
  total_reviews?: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ShopProduct {
  id: string;
  shopId?: string;
  shop_id?: string;
  name: string;
  price: number;
  category: string;
  category_id?: string;
  topCategory: string;
  brand?: string;
  weight?: string;
  description?: string;
  sku?: string;
  stock?: number;
  stock_quantity?: number;
  sales?: number;
  revenue?: number;
  image: string;
  rating: number;
  reviews: number;
  inStock: boolean;
  discount: number;
  isActive?: boolean;
  is_active?: boolean;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  tags?: string[];
}

export interface ShopCategory {
  id: string;
  shopId: string;
  name: string;
  displayName: string;
  icon: string;
  topCategory: string;
  description?: string;
  parentId?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ShopOrder {
  id: string;
  shopId: string;
  customerId: string;
  customerName: string;
  items: ShopOrderItem[];
  total: number;
  status: 'pending' | 'processing' | 'ready' | 'delivered' | 'cancelled';
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded';
  shippingAddress: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShopOrderItem {
  productId: string;
  productName: string;
  quantity: number;
  price: number;
  total: number;
}

export interface ShopCustomer {
  id: string;
  shopId: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  totalOrders: number;
  totalSpent: number;
  avgOrderValue: number;
  lastOrderDate?: string;
  isValuable: boolean;
  isPremium: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

class ShopService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    // Use shopServiceUrl from config (now points to API gateway on port 3000)
    // This matches the shop dashboard endpoint which returns logos
    this.baseUrl = baseUrl || (apiConfig as any).shopServiceUrl || 'http://localhost:3000/api';
    
    console.log('🏪 ShopService initialized with URL:', this.baseUrl);
    console.log('📍 Using API gateway endpoint (same as shop dashboard) to get shop logos');
  }

  // Helper function to normalize shop types (matching mobile app logic)
  // Handles database values like "Bakeries" -> "bakery"
  private normalizeShopType(type: string | undefined): Shop['type'] {
    if (!type) return 'specialty';
    
    const normalized = type.toLowerCase().trim();
    
    // Map plural/variations to singular valid types (matching database values like "Bakeries")
    const typeMapping: { [key: string]: Shop['type'] } = {
      'bakeries': 'bakery',
      'bakery': 'bakery',
      'baker': 'bakery',
      'groceries': 'grocery',
      'grocery': 'grocery',
      'kirana': 'grocery',
      'mart': 'grocery',
      'pharmacy': 'pharmacy',
      'pharmacies': 'pharmacy',
      'medical': 'medical',
      'electronics': 'electronics',
      'clothing': 'clothing',
      'restaurant': 'restaurant',
      'restaurants': 'restaurant',
      'specialty': 'specialty',
      'specialties': 'specialty'
    };
    
    // Check direct mapping first
    if (typeMapping[normalized]) {
      return typeMapping[normalized];
    }
    
    // Check if it matches any valid type
    const validTypes: Shop['type'][] = ['grocery', 'medical', 'electronics', 'clothing', 'pharmacy', 'specialty', 'bakery', 'restaurant'];
    if (validTypes.includes(normalized as Shop['type'])) {
      return normalized as Shop['type'];
    }
    
    // Default to specialty if no match
    return 'specialty';
  }

  private getHeaders(): Record<string, string> {
    // Use 'access_token' to match AuthContext (not 'authToken')
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // Shop Management
  async getShops(): Promise<Shop[]> {
    try {
      // Handle both relative URLs (staging) and absolute URLs (local)
      let url: string;
      if (this.baseUrl.startsWith('/')) {
        // Relative URL (staging) - use string concatenation with URLSearchParams
        const params = new URLSearchParams();
        params.append('limit', '1000');
        params.append('page', '1');
        params.append('all', 'true');
        url = `${this.baseUrl}/shops?${params.toString()}`;
      } else {
        // Absolute URL (local) - use URL constructor
        const urlObj = new URL(`${this.baseUrl}/shops`);
        urlObj.searchParams.append('limit', '1000');
        urlObj.searchParams.append('page', '1');
        urlObj.searchParams.append('all', 'true');
        url = urlObj.toString();
      }
      
      console.log('📡 ShopService HTTP Request:', {
        method: 'GET',
        url: url,
        headers: this.getHeaders()
      });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      
      console.log('📥 ShopService HTTP Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch shops: ${response.status}`);
      }
      
      const data = await response.json();
      const shops = data.shops || data;
      
      console.log('✅ ShopService shops received:', {
        totalShops: Array.isArray(shops) ? shops.length : 0,
        shopNames: Array.isArray(shops) ? shops.map((s: any) => s.name) : []
      });
      
      // Log the equivalent SQL query that would be executed on the backend
      console.log('💾 ShopService Equivalent SQL Query (executed on backend):');
      console.log(`
        SELECT 
          s.*,
          COUNT(DISTINCT p.id) as product_count
        FROM shops s
        LEFT JOIN products p ON p.shop_id = s.id
        WHERE s.is_active = true
        GROUP BY s.id
        ORDER BY s.created_at DESC
        LIMIT 1000;
      `);
      
      // Normalize shop data to ensure logo field is properly mapped from various image field names
      const normalizedShops: Shop[] = shops.map((shop: any) => {
        // Check all possible image field names (in order of preference)
        const rawLogo = shop.logo || shop.logo_url || shop.image || shop.image_url || shop.photo || shop.banner_url || undefined;
        console.log(`ShopService: Shop "${shop.name}" - checking image fields:`, {
          logo: shop.logo,
          logo_url: shop.logo_url,
          image: shop.image,
          image_url: shop.image_url,
          photo: shop.photo,
          banner_url: shop.banner_url,
          selected: rawLogo
        });
        return {
          ...shop,
          logo: rawLogo,
          type: this.normalizeShopType(shop.type) // Normalize shop type (e.g., "Bakeries" -> "bakery")
        };
      });
      
      console.log('ShopService: Normalized shops with logos and types:', normalizedShops.map(s => ({ name: s.name, logo: s.logo, type: s.type, originalType: (shops.find((orig: any) => orig.id === s.id) as any)?.type })));
      
      return normalizedShops;
    } catch (error) {
      console.error('Error fetching shops:', error);
      // Return mock data for development
      return this.getMockShops();
    }
  }

  async getShop(shopId: string): Promise<Shop> {
    try {
      const response = await fetch(`${this.baseUrl}/shops/${shopId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch shop: ${response.status}`);
      }
      
      const shop = await response.json();
      
      // Normalize shop data to ensure logo field is properly mapped from various image field names
      // This matches the same normalization logic used in getShops() to ensure consistency
      const rawLogo = shop.logo || shop.logo_url || shop.image || shop.image_url || shop.photo || shop.banner_url || undefined;
      console.log(`ShopService.getShop: Shop "${shop.name}" - checking image fields:`, {
        logo: shop.logo,
        logo_url: shop.logo_url,
        image: shop.image,
        image_url: shop.image_url,
        photo: shop.photo,
        banner_url: shop.banner_url,
        selected: rawLogo
      });
      
      return {
        ...shop,
        logo: rawLogo,
        type: this.normalizeShopType(shop.type) // Normalize shop type (e.g., "Bakeries" -> "bakery")
      };
    } catch (error) {
      console.error('Error fetching shop:', error);
      // Return mock data for development
      const shops = this.getMockShops();
      return shops.find(shop => shop.id === shopId) || shops[0];
    }
  }

  // Shop-Specific Product Management
  async getShopProducts(shopId?: string): Promise<ShopProduct[]> {
    if (!shopId) {
      throw new Error('No shop ID provided');
    }

    try {
      const response = await fetch(`${this.baseUrl}/shops/${shopId}/products`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch shop products: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching shop products:', error);
      // Return mock data for development
      return this.getMockShopProducts(shopId);
    }
  }

  // Cross-Shop Product Search (for customers)
  async searchAllShops(query: string, filters?: {
    shopType?: string;
    category?: string;
    minPrice?: number;
    maxPrice?: number;
  }): Promise<{ shop: Shop; products: ShopProduct[] }[]> {
    try {
      const params = new URLSearchParams();
      params.append('q', query);
      if (filters?.shopType) params.append('shopType', filters.shopType);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.minPrice) params.append('minPrice', filters.minPrice.toString());
      if (filters?.maxPrice) params.append('maxPrice', filters.maxPrice.toString());

      const response = await fetch(`${this.baseUrl}/search?${params}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to search products: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error searching products:', error);
      // Return mock data for development
      return this.getMockSearchResults(query, filters);
    }
  }

  // Mock Data for Development
  private getMockShops(): Shop[] {
    return [
      {
        id: 'shop-1',
        name: 'Downtown Grocery Store',
        address: '123 Main St, Downtown',
        city: 'New York',
        state: 'NY',
        country: 'USA',
        owner: 'John Smith',
        type: 'grocery',
        description: 'Fresh groceries and household items',
        phone: '+1-555-0101',
        email: 'downtown@grocerystore.com',
        is_active: true,
        theme: {
          primary_color: '#4ade80',
          secondary_color: '#22c55e',
          accent_color: '#16a34a'
        },
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      {
        id: 'shop-medical-1',
        name: 'City Medical Supplies',
        address: '100 Health Plaza, Medical District',
        city: 'New York',
        state: 'NY',
        country: 'USA',
        owner: 'Dr. Sarah Chen',
        type: 'medical',
        description: 'Professional medical supplies and equipment',
        phone: '+1-555-0201',
        email: 'info@citymedical.com',
        is_active: true,
        theme: {
          primary_color: '#ef4444',
          secondary_color: '#dc2626',
          accent_color: '#b91c1c'
        },
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      {
        id: 'shop-electronics-1',
        name: 'Tech Hub Electronics',
        address: '500 Tech Ave, Innovation District',
        city: 'San Francisco',
        state: 'CA',
        country: 'USA',
        owner: 'Mike Johnson',
        type: 'electronics',
        description: 'Latest electronics and gadgets',
        phone: '+1-555-0301',
        email: 'sales@techhub.com',
        is_active: true,
        theme: {
          primary_color: '#8b5cf6',
          secondary_color: '#7c3aed',
          accent_color: '#6d28d9'
        },
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      {
        id: 'shop-clothing-1',
        name: 'Fashion Forward',
        address: '300 Style Blvd, Fashion District',
        city: 'Los Angeles',
        state: 'CA',
        country: 'USA',
        owner: 'Emma Wilson',
        type: 'clothing',
        description: 'Trendy clothing and accessories',
        phone: '+1-555-0401',
        email: 'info@fashionforward.com',
        is_active: true,
        theme: {
          primary_color: '#ec4899',
          secondary_color: '#db2777',
          accent_color: '#be185d'
        },
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      }
    ];
  }

  private getMockShopProducts(shopId: string): ShopProduct[] {
    // Return different products based on shop type
    const shop = this.getMockShops().find(s => s.id === shopId);
    if (!shop) return [];

    if (shop.type === 'medical') {
      return [
        {
          id: 'med-1',
          shopId: shopId,
          name: 'Metformin 500mg (30 tablets)',
          price: 12.99,
          category: 'prescription-meds',
          topCategory: 'medical',
          brand: 'Generic',
          weight: '30 tablets',
          description: 'Diabetes medication for blood sugar control',
          sku: 'MET500-30',
          stock: 25,
          sales: 15,
          revenue: 194.85,
          image: '💊',
          rating: 4.7,
          reviews: 89,
          inStock: true,
          discount: 0,
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        },
        {
          id: 'med-2',
          shopId: shopId,
          name: 'Digital Blood Pressure Monitor',
          price: 49.99,
          category: 'medical-equipment',
          topCategory: 'medical',
          brand: 'Omron',
          weight: '1 unit',
          description: 'Digital blood pressure monitoring device',
          sku: 'BP-MON-001',
          stock: 15,
          sales: 8,
          revenue: 399.92,
          image: '🩺',
          rating: 4.7,
          reviews: 89,
          inStock: true,
          discount: 15,
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        },
        {
          id: 'med-3',
          shopId: shopId,
          name: 'First Aid Kit (50 pieces)',
          price: 19.99,
          category: 'first-aid',
          topCategory: 'medical',
          brand: 'Johnson & Johnson',
          weight: '50 pieces',
          description: 'Comprehensive first aid kit for emergencies',
          sku: 'FAK-50',
          stock: 30,
          sales: 25,
          revenue: 499.75,
          image: '🩹',
          rating: 4.8,
          reviews: 234,
          inStock: true,
          discount: 0,
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        }
      ];
    } else if (shop.type === 'grocery') {
      return [
        {
          id: 'groc-1',
          shopId: shopId,
          name: 'Aashirvaad Whole Wheat Atta',
          price: 4.99,
          category: 'grains-flours',
          topCategory: 'groceries',
          brand: 'Aashirvaad',
          weight: '5kg',
          description: 'Premium whole wheat flour for healthy cooking',
          sku: 'AWW-5KG',
          stock: 45,
          sales: 23,
          revenue: 114.77,
          image: '🌾',
          rating: 4.8,
          reviews: 156,
          inStock: true,
          discount: 0,
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        },
        {
          id: 'groc-2',
          shopId: shopId,
          name: 'Fresh Organic Bananas',
          price: 2.99,
          category: 'fruits-vegetables',
          topCategory: 'groceries',
          brand: 'Organic Farm',
          weight: '1kg',
          description: 'Fresh organic bananas, perfect for snacking',
          sku: 'BAN-ORG-1KG',
          stock: 60,
          sales: 45,
          revenue: 134.55,
          image: '🍌',
          rating: 4.6,
          reviews: 89,
          inStock: true,
          discount: 10,
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        }
      ];
    } else if (shop.type === 'electronics') {
      return [
        {
          id: 'elec-1',
          shopId: shopId,
          name: 'Smartphone Pro Max',
          price: 299.99,
          category: 'smartphones',
          topCategory: 'electronics',
          brand: 'TechBrand',
          weight: '200g',
          description: 'Latest smartphone with advanced features and camera',
          sku: 'SM-PRO-MAX',
          stock: 15,
          sales: 8,
          revenue: 2399.92,
          image: '📱',
          rating: 4.8,
          reviews: 342,
          inStock: true,
          discount: 25,
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        },
        {
          id: 'elec-2',
          shopId: shopId,
          name: 'Wireless Bluetooth Headphones',
          price: 79.99,
          category: 'audio',
          topCategory: 'electronics',
          brand: 'SoundMax',
          weight: '250g',
          description: 'High-quality wireless headphones with noise cancellation',
          sku: 'WBH-NC-001',
          stock: 25,
          sales: 18,
          revenue: 1439.82,
          image: '🎧',
          rating: 4.5,
          reviews: 127,
          inStock: true,
          discount: 15,
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        }
      ];
    } else if (shop.type === 'clothing') {
      return [
        {
          id: 'cloth-1',
          shopId: shopId,
          name: 'Designer T-Shirt',
          price: 29.99,
          category: 'mens-clothing',
          topCategory: 'clothing',
          brand: 'StyleCo',
          weight: '150g',
          description: 'Comfortable cotton t-shirt with modern design',
          sku: 'TS-DES-001',
          stock: 40,
          sales: 25,
          revenue: 749.75,
          image: '👕',
          rating: 4.6,
          reviews: 89,
          inStock: true,
          discount: 20,
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        }
      ];
    }

    return [];
  }

  private getMockSearchResults(query: string, _filters?: any): { shop: Shop; products: ShopProduct[] }[] {
    const shops = this.getMockShops();
    const results: { shop: Shop; products: ShopProduct[] }[] = [];

    shops.forEach(shop => {
      const products = this.getMockShopProducts(shop.id);
      const filteredProducts = products.filter(product => 
        product.name.toLowerCase().includes(query.toLowerCase()) ||
        product.description?.toLowerCase().includes(query.toLowerCase()) ||
        product.brand?.toLowerCase().includes(query.toLowerCase())
      );
      
      if (filteredProducts.length > 0) {
        results.push({ shop, products: filteredProducts });
      }
    });

    return results;
  }
}

// Create and export singleton instance
export const shopService = new ShopService();
export default ShopService;
